import { useState } from 'react';
import { Bell, Trash2, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader, CardTitle, CardContent, StatCard } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { RiskBadge } from '@/components/ui/RiskBadge';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import { StatField } from '@/components/ui/StatField';
import { StatTile } from '@/components/ui/StatTile';
import { Input, Textarea, NativeSelect } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { LoadingState, EmptyState, ErrorState } from '@/components/ui/States';
import { TabBar } from '@/components/ui/TabBar';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/Dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/Select';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from '@/components/ui/DropdownMenu';
import { Checkbox } from '@/components/ui/Checkbox';
import { Switch } from '@/components/ui/Switch';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/Tooltip';
import { ComponentPreview, Section } from './ComponentPreview';

/**
 * HMS's answer to PulseOS's /design-system page (see
 * HMS_UI_REDESIGN_ANALYSIS.md — "Build a Reusable HMS Design System" was
 * always Phase 1 of the redesign plan; this is the missing explainer view
 * for it, added once the component library existed to document). A live
 * gallery of every component in src/components/ui/, so a screen being
 * migrated has one place to check what already exists before hand-rolling
 * another modal or table.
 *
 * Every swatch below reads the hospital's own runtime branding — this page
 * repaints along with the rest of the app when a hospital changes its
 * colors in the Branding tab, the same way every other migrated screen does.
 */
export default function DesignSystemTab() {
  const [tabBarActive, setTabBarActive] = useState<'a' | 'b' | 'c'>('a');
  const [checked, setChecked] = useState(true);
  const [switchOn, setSwitchOn] = useState(true);
  const [riskLevel, setRiskLevel] = useState<'Low' | 'Medium' | 'High' | 'Critical'>('Medium');
  const [diet, setDiet] = useState<string[]>(['Protein']);

  return (
    <div className="fade-in space-y-10">
      <div>
        <div className="text-lg font-bold text-ink">HMS Design System</div>
        <p className="mt-1 max-w-2xl text-[13px] leading-relaxed text-ink-muted">
          A live reference for every component in <code className="font-mono text-xs">src/components/ui/</code>. Colors and radius
          follow this hospital's own branding — change it in the Branding tab and this page repaints too. Structure and interaction
          patterns are referenced from PulseOS; the values are HMS's.
        </p>
      </div>

      {/* ── Foundation ──────────────────────────────────────────────── */}
      <Section icon="🎨" title="Foundation">
        <ComponentPreview
          title="Brand colors"
          description="Runtime — set per hospital in the Branding tab"
          code={`bg-teal        /* primary action */
bg-navy        /* sidebar / header */
bg-accent      /* teal tint, badges */
text-ink       /* body text */
text-ink-muted /* secondary text */
bg-canvas      /* page background */
bg-card        /* surface */`}
        >
          <div className="grid w-full grid-cols-3 gap-3 sm:grid-cols-6">
            {[
              { cls: 'bg-teal', label: 'teal' },
              { cls: 'bg-navy', label: 'navy' },
              { cls: 'bg-accent', label: 'accent' },
              { cls: 'bg-canvas border border-line', label: 'canvas' },
              { cls: 'bg-card border border-line', label: 'card' },
              { cls: 'bg-ink', label: 'ink' },
            ].map((s) => (
              <div key={s.label} className="text-center">
                <div className={`mb-1.5 h-12 w-full rounded-lg ${s.cls}`} />
                <div className="text-[10px] font-semibold text-ink-muted">{s.label}</div>
              </div>
            ))}
          </div>
        </ComponentPreview>

        <ComponentPreview
          title="Status colors"
          description="Fixed — never brand-tinted, see theme.css"
          code={`bg-success-soft text-success-ink
bg-warn-soft    text-warn-ink
bg-danger-soft  text-danger-ink
bg-info-soft    text-info-ink`}
        >
          <div className="grid w-full grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { bg: 'bg-success-soft', text: 'text-success-ink', label: 'success' },
              { bg: 'bg-warn-soft', text: 'text-warn-ink', label: 'warn' },
              { bg: 'bg-danger-soft', text: 'text-danger-ink', label: 'danger' },
              { bg: 'bg-info-soft', text: 'text-info-ink', label: 'info' },
            ].map((s) => (
              <div key={s.label} className={`rounded-lg px-3 py-2.5 text-center text-xs font-bold ${s.bg} ${s.text}`}>
                {s.label}
              </div>
            ))}
          </div>
        </ComponentPreview>

        <ComponentPreview
          title="Radius scale"
          description="Re-based on the hospital's runtime --radius"
          code={`rounded-sm  /* calc(radius - 4px) */
rounded-md  /* calc(radius - 2px) */
rounded-lg  /* radius */
rounded-xl  /* calc(radius + 4px) */`}
        >
          <div className="flex flex-wrap items-end gap-4">
            {['sm', 'md', 'lg', 'xl'].map((r) => (
              <div key={r} className="text-center">
                <div className={`mb-1.5 h-14 w-14 rounded-${r} border-2 border-teal bg-accent`} />
                <div className="text-[10px] font-semibold text-ink-muted">rounded-{r}</div>
              </div>
            ))}
          </div>
        </ComponentPreview>

        <ComponentPreview
          title="Typography"
          code={`<div className="text-lg font-bold">Screen title</div>
<div className="text-sm font-semibold">Card heading</div>
<div className="text-[13px] text-ink-muted">Body text</div>
<label className="lbl">Field label</label>`}
        >
          <div className="w-full space-y-2">
            <div className="text-lg font-bold text-ink">Screen title</div>
            <div className="text-sm font-semibold text-ink">Card heading</div>
            <div className="text-[13px] text-ink-muted">Body text — used for descriptions and secondary content.</div>
            <label className="lbl block">Field label</label>
          </div>
        </ComponentPreview>
      </Section>

      {/* ── Buttons ─────────────────────────────────────────────────── */}
      <Section icon="🔘" title="Buttons">
        <ComponentPreview
          title="Variants"
          code={`<Button>Primary</Button>
<Button variant="ghost">Ghost</Button>
<Button variant="success">Success</Button>
<Button variant="danger">Danger</Button>
<Button variant="link">Link</Button>`}
        >
          <Button>Primary</Button>
          <Button variant="ghost">Ghost</Button>
          <Button variant="success">Success</Button>
          <Button variant="danger">Danger</Button>
          <Button variant="link">Link</Button>
        </ComponentPreview>

        <ComponentPreview
          title="Sizes, states & icon"
          code={`<Button size="sm">Small</Button>
<Button size="lg">Large</Button>
<Button loading>Saving…</Button>
<Button disabled>Disabled</Button>
<Button size="icon"><Bell className="h-4 w-4" /></Button>`}
        >
          <Button size="sm">Small</Button>
          <Button size="lg">Large</Button>
          <Button loading>Saving…</Button>
          <Button disabled>Disabled</Button>
          <Button size="icon"><Bell className="h-4 w-4" /></Button>
        </ComponentPreview>
      </Section>

      {/* ── Layout ──────────────────────────────────────────────────── */}
      <Section icon="🗂" title="Layout">
        <ComponentPreview
          title="Card"
          code={`<Card>
  <CardHeader><CardTitle>Title</CardTitle></CardHeader>
  <CardContent>Body content.</CardContent>
</Card>`}
        >
          <Card className="w-full max-w-xs">
            <CardHeader><CardTitle>Card title</CardTitle></CardHeader>
            <CardContent className="text-[13px] text-ink-muted">The default surface for grouped content across the app.</CardContent>
          </Card>
        </ComponentPreview>

        <ComponentPreview
          title="StatCard"
          code={`<StatCard accentColor="var(--teal)">
  <div className="text-[32px] font-bold">42</div>
  <div className="text-ink-muted">Label</div>
</StatCard>`}
        >
          <StatCard accentColor="var(--teal)" className="w-full max-w-[180px]">
            <div className="text-[10px] font-bold uppercase tracking-wide text-ink-muted">Waiting</div>
            <div className="text-[28px] font-bold">12</div>
          </StatCard>
        </ComponentPreview>

        <ComponentPreview
          title="TabBar — pill row (page-section switcher)"
          code={`<TabBar
  tabs={[{ id: 'a', label: 'Tab A' }, { id: 'b', label: 'Tab B' }]}
  active={active}
  onChange={setActive}
/>`}
        >
          <TabBar
            tabs={[{ id: 'a' as const, label: 'Users' }, { id: 'b' as const, label: 'Roles' }, { id: 'c' as const, label: 'Settings' }]}
            active={tabBarActive}
            onChange={setTabBarActive}
          />
        </ComponentPreview>

        <ComponentPreview
          title="Tabs — full ARIA tablist"
          description="For tabs nested inside a Dialog or dense form"
          code={`<Tabs defaultValue="one">
  <TabsList>
    <TabsTrigger value="one">One</TabsTrigger>
    <TabsTrigger value="two">Two</TabsTrigger>
  </TabsList>
  <TabsContent value="one">...</TabsContent>
</Tabs>`}
        >
          <Tabs defaultValue="one" className="w-full max-w-xs">
            <TabsList className="w-full">
              <TabsTrigger value="one" className="flex-1">One</TabsTrigger>
              <TabsTrigger value="two" className="flex-1">Two</TabsTrigger>
            </TabsList>
            <TabsContent value="one" className="text-[13px] text-ink-muted">Content for tab one.</TabsContent>
            <TabsContent value="two" className="text-[13px] text-ink-muted">Content for tab two.</TabsContent>
          </Tabs>
        </ComponentPreview>

        <ComponentPreview
          title="Table"
          code={`<Table>
  <TableHeader><TableRow><TableHead>Name</TableHead></TableRow></TableHeader>
  <TableBody><TableRow><TableCell>Row</TableCell></TableRow></TableBody>
</Table>`}
        >
          <Table className="w-full">
            <TableHeader>
              <TableRow><TableHead>Medicine</TableHead><TableHead>Stock</TableHead><TableHead>Status</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              <TableRow><TableCell className="font-semibold">Paracetamol 500</TableCell><TableCell>240</TableCell><TableCell><Badge variant="success">OK</Badge></TableCell></TableRow>
              <TableRow><TableCell className="font-semibold">Amoxicillin 250</TableCell><TableCell>8</TableCell><TableCell><Badge variant="warn">Low stock</Badge></TableCell></TableRow>
            </TableBody>
          </Table>
        </ComponentPreview>

        <ComponentPreview
          title="Loading / Empty / Error states"
          code={`<LoadingState label="Loading…" />
<EmptyState label="No results" hint="Try a different filter" />
<ErrorState message="Failed to load" onRetry={refetch} />`}
        >
          <div className="w-full space-y-2">
            <LoadingState label="Loading…" />
            <EmptyState label="No results" hint="Try a different filter" />
            <ErrorState message="Failed to load records" onRetry={() => {}} />
          </div>
        </ComponentPreview>
      </Section>

      {/* ── Forms ───────────────────────────────────────────────────── */}
      <Section icon="📝" title="Forms">
        <ComponentPreview
          title="FormField + Input / Textarea"
          code={`<FormField label="Full name" required>
  <Input placeholder="e.g. Ananya Kumar" />
</FormField>
<FormField label="Notes">
  <Textarea placeholder="Optional" />
</FormField>`}
        >
          <div className="w-full max-w-xs space-y-3">
            <FormField label="Full name" required>
              <Input placeholder="e.g. Ananya Kumar" />
            </FormField>
            <FormField label="Notes">
              <Textarea placeholder="Optional" />
            </FormField>
          </div>
        </ComponentPreview>

        <ComponentPreview
          title="Native select vs. Select (Radix)"
          description="Native for a short plain list; Select for a richer dropdown"
          code={`<NativeSelect><option>Doctor</option></NativeSelect>

<Select>
  <SelectTrigger><SelectValue placeholder="Role" /></SelectTrigger>
  <SelectContent><SelectItem value="doctor">Doctor</SelectItem></SelectContent>
</Select>`}
        >
          <div className="w-full max-w-xs space-y-3">
            <NativeSelect defaultValue="doctor">
              <option value="doctor">Doctor</option>
              <option value="nurse">Nurse</option>
            </NativeSelect>
            <Select defaultValue="doctor">
              <SelectTrigger><SelectValue placeholder="Role" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="doctor">Doctor</SelectItem>
                <SelectItem value="nurse">Nurse</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </ComponentPreview>

        <ComponentPreview
          title="Checkbox & Switch"
          code={`<Checkbox checked={v} onCheckedChange={setV} />
<Switch checked={v} onCheckedChange={setV} />`}
        >
          <label className="flex items-center gap-2 text-[13px]">
            <Checkbox checked={checked} onCheckedChange={(v) => setChecked(!!v)} />
            Accept terms
          </label>
          <label className="flex items-center gap-2 text-[13px]">
            <Switch checked={switchOn} onCheckedChange={setSwitchOn} />
            Auto-reassign
          </label>
        </ComponentPreview>
      </Section>

      {/* ── Overlays ────────────────────────────────────────────────── */}
      <Section icon="🪟" title="Overlays">
        <ComponentPreview
          title="Dialog"
          code={`<Dialog>
  <DialogTrigger asChild><Button>Open</Button></DialogTrigger>
  <DialogContent>
    <DialogHeader><DialogTitle>Title</DialogTitle></DialogHeader>
    <DialogFooter><Button>Confirm</Button></DialogFooter>
  </DialogContent>
</Dialog>`}
        >
          <Dialog>
            <DialogTrigger asChild><Button variant="ghost">Open dialog</Button></DialogTrigger>
            <DialogContent className="max-w-[380px]">
              <DialogHeader><DialogTitle>Example dialog</DialogTitle></DialogHeader>
              <div className="text-[13px] text-ink-muted">Replaces the fixed-position overlay divs every screen used to hand-roll.</div>
              <DialogFooter><Button>Confirm</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </ComponentPreview>

        <ComponentPreview
          title="DropdownMenu"
          code={`<DropdownMenu>
  <DropdownMenuTrigger asChild><Button variant="ghost">Actions</Button></DropdownMenuTrigger>
  <DropdownMenuContent>
    <DropdownMenuItem>Edit</DropdownMenuItem>
    <DropdownMenuItem>Delete</DropdownMenuItem>
  </DropdownMenuContent>
</DropdownMenu>`}
        >
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost">Actions <ChevronDown className="ml-1 h-3.5 w-3.5" /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem>Edit</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-danger"><Trash2 className="mr-2 h-3.5 w-3.5" />Delete</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </ComponentPreview>

        <ComponentPreview
          title="Tooltip"
          code={`<Tooltip>
  <TooltipTrigger asChild><Button size="icon">?</Button></TooltipTrigger>
  <TooltipContent>Helper text</TooltipContent>
</Tooltip>`}
        >
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild><Button variant="ghost" size="icon">?</Button></TooltipTrigger>
              <TooltipContent>Helper text on hover</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </ComponentPreview>

        <ComponentPreview
          title="Badge — status variants"
          description="Fixed clinical/financial meaning, never brand-tinted"
          code={`<Badge variant="success">Active</Badge>
<Badge variant="warn">Pending</Badge>
<Badge variant="danger">Expired</Badge>
<Badge variant="info">Info</Badge>
<Badge variant="caution">Schedule H1</Badge>
<Badge>Neutral</Badge>`}
        >
          <Badge variant="success">Active</Badge>
          <Badge variant="warn">Pending</Badge>
          <Badge variant="danger">Expired</Badge>
          <Badge variant="info">Info</Badge>
          <Badge variant="caution">Schedule H1</Badge>
          <Badge>Neutral</Badge>
        </ComponentPreview>

        <ComponentPreview
          title="RiskBadge — severity verdict"
          description="Bigger, bolder than Badge — for a glance-from-across-the-room verdict (Risk level, Assessment Module overall)"
          code={`<RiskBadge tone="success">Low</RiskBadge>
<RiskBadge tone="warn">Medium</RiskBadge>
<RiskBadge tone="caution">High</RiskBadge>
<RiskBadge tone="danger">Critical</RiskBadge>`}
        >
          <RiskBadge tone="success">Low</RiskBadge>
          <RiskBadge tone="warn">Medium</RiskBadge>
          <RiskBadge tone="caution">High</RiskBadge>
          <RiskBadge tone="danger">Critical</RiskBadge>
        </ComponentPreview>
      </Section>

      {/* ── Selection ───────────────────────────────────────────────── */}
      <Section icon="🎛" title="Selection">
        <ComponentPreview
          title="SegmentedControl — single select (radio)"
          description="Toned per-option via RISK_LEVEL_TONE — an active option colors itself the same way a RiskBadge for that value would"
          code={`<SegmentedControl
  options={[
    { value: 'Low', label: 'Low', tone: 'success' },
    { value: 'Medium', label: 'Medium', tone: 'warn' },
    { value: 'High', label: 'High', tone: 'caution' },
    { value: 'Critical', label: 'Critical', tone: 'danger' },
  ]}
  value={riskLevel}
  onChange={setRiskLevel}
/>`}
        >
          <SegmentedControl
            options={[
              { value: 'Low' as const, label: 'Low', tone: 'success' },
              { value: 'Medium' as const, label: 'Medium', tone: 'warn' },
              { value: 'High' as const, label: 'High', tone: 'caution' },
              { value: 'Critical' as const, label: 'Critical', tone: 'danger' },
            ]}
            value={riskLevel}
            onChange={setRiskLevel}
          />
        </ComponentPreview>

        <ComponentPreview
          title="SegmentedControl — multi select (checkbox-group)"
          description="multiple — untoned options fall back to the default teal-accent selection style"
          code={`<SegmentedControl
  multiple
  options={[{ value: 'Protein', label: 'Protein' }, { value: 'Fibre', label: 'Fibre' }, { value: 'Carbs', label: 'Carbs' }]}
  value={diet}
  onChange={setDiet}
/>`}
        >
          <SegmentedControl
            multiple
            options={[
              { value: 'Protein', label: 'Protein' },
              { value: 'Fibre', label: 'Fibre' },
              { value: 'Carbs', label: 'Carbs' },
            ]}
            value={diet}
            onChange={setDiet}
          />
        </ComponentPreview>

        <ComponentPreview
          title="StatField — icon-topped input tile"
          description="Editable sibling of StageSummaryView's read-only StatTile — same chrome, wraps a real input"
          code={`<StatField icon="🫀" label="Pulse (bpm)">
  <Input type="number" placeholder="e.g. 78" />
</StatField>
<StatField icon="🌡️" label="Temp (°F)" warn>
  <Input type="number" defaultValue="101.4" />
</StatField>`}
        >
          <StatField icon="🫀" label="Pulse (bpm)">
            <Input type="number" placeholder="e.g. 78" />
          </StatField>
          <StatField icon="🌡️" label="Temp (°F)" warn>
            <Input type="number" defaultValue="101.4" />
          </StatField>
        </ComponentPreview>

        <ComponentPreview
          title="StatTile — read-only sibling"
          description="Cascaded from Vitals into both the Consultation sidebar and Pre-Consult's read-only strip — same component, so a reading looks identical wherever it resurfaces"
          code={`<StatTile icon="🫀" label="BP" value="130/80" unit="mmHg" />
<StatTile icon="🌡️" label="Temp" value="101.4" unit="°F" warn />`}
        >
          <StatTile icon="🫀" label="BP" value="130/80" unit="mmHg" />
          <StatTile icon="🌡️" label="Temp" value="101.4" unit="°F" warn />
        </ComponentPreview>
      </Section>
    </div>
  );
}
